import { useState, useMemo, useCallback, memo } from 'react';
import { useGitHubInstallations, useInstallationRepositories, GitHubRepository } from '../hooks/useGitHub';
import { Search, Check, Loader2, AlertCircle } from 'lucide-react';

interface RepoPickerProps {
  value: {
    installationId: string;
    organization: string;
    repositories: string[];
  } | null;
  onChange: (selection: {
    installationId: string;
    organization: string;
    repositories: string[];
  }) => void;
  multiSelect?: boolean;
  className?: string;
}

/**
 * Memoized repository item to prevent re-rendering when selection changes
 */
interface RepositoryItemProps {
  repo: GitHubRepository;
  isSelected: boolean;
  onToggle: (repo: GitHubRepository) => void;
}

const RepositoryItem = memo(function RepositoryItem({ repo, isSelected, onToggle }: RepositoryItemProps) {
  return (
    <button
      onClick={() => onToggle(repo)}
      className={`
        w-full px-3 py-2 rounded-md text-left transition-colors flex items-center justify-between
        ${isSelected
          ? 'bg-blue-600/20 border border-blue-500/50'
          : 'hover:bg-slate-700/50 border border-transparent'
        }
      `}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium truncate">{repo.name}</span>
          {repo.isPrivate && (
            <span className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded">
              Private
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate">{repo.fullName}</p>
      </div>
      {isSelected && (
        <Check className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2" />
      )}
    </button>
  );
});

/**
 * RepoPicker - Repository selection component with installation scoping
 * 
 * Features:
 * - Installation selection dropdown
 * - Repository search and filtering
 * - Multi-select or single-select mode
 * - Real-time filtering as user types
 */
export function RepoPicker({ value, onChange, multiSelect = true, className = '' }: RepoPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstallationId, setSelectedInstallationId] = useState<string | null>(
    value?.installationId || null
  );

  const { data: installations, isLoading: installationsLoading, error: installationsError } = 
    useGitHubInstallations();
  
  // Find the selected installation and get its numeric installationId
  const selectedInstallation = useMemo(
    () => installations?.find(i => i.id === selectedInstallationId),
    [installations, selectedInstallationId]
  );
  const numericInstallationId = useMemo(
    () => selectedInstallation?.installationId ?? null,
    [selectedInstallation?.installationId]
  );
  
  const { data: repositories, isLoading: reposLoading, error: reposError } = 
    useInstallationRepositories(numericInstallationId);

  const selectedRepoNames = useMemo(() => new Set(value?.repositories || []), [value?.repositories]);

  const handleInstallationChange = useCallback((installationId: string) => {
    setSelectedInstallationId(installationId);
    const installation = installations?.find(i => i.id === installationId);
    
    // Reset selection when changing installation
    onChange({
      installationId,
      organization: installation?.accountLogin || '',
      repositories: [],
    });
  }, [installations, onChange]);

  const handleRepoToggle = useCallback((repo: GitHubRepository) => {
    if (!selectedInstallationId) return;

    const installation = installations?.find(i => i.id === selectedInstallationId);
    if (!installation) return;

    let newRepos: string[];
    
    if (multiSelect) {
      if (selectedRepoNames.has(repo.name)) {
        newRepos = value!.repositories.filter(r => r !== repo.name);
      } else {
        newRepos = [...(value?.repositories || []), repo.name];
      }
    } else {
      newRepos = [repo.name];
    }

    onChange({
      installationId: selectedInstallationId,
      organization: installation.accountLogin,
      repositories: newRepos,
    });
  }, [selectedInstallationId, installations, selectedRepoNames, multiSelect, value, onChange]);

  const filteredRepos = useMemo(() => 
    repositories?.filter(repo =>
      repo.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      repo.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [], 
    [repositories, searchQuery]
  );

  if (installationsLoading) {
    return (
      <div className={`border border-slate-700 rounded-lg p-6 bg-slate-800 ${className}`}>
        <div className="flex items-center justify-center gap-2 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading installations...</span>
        </div>
      </div>
    );
  }

  if (installationsError || !installations) {
    return (
      <div className={`border border-red-900/50 rounded-lg p-6 bg-red-950/20 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-medium mb-1">Failed to load GitHub installations</p>
            <p className="text-red-300/70 text-sm">
              Please ensure your GitHub App is properly connected.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (installations.length === 0) {
    return (
      <div className={`border border-slate-700 rounded-lg p-6 bg-slate-800 ${className}`}>
        <p className="text-slate-400 text-center">
          No GitHub installations found. Please connect your GitHub App first.
        </p>
      </div>
    );
  }

  return (
    <div className={`border border-slate-700 rounded-lg bg-slate-800 ${className}`}>
      {/* Installation Selector */}
      <div className="p-4 border-b border-slate-700">
        <label className="block text-sm font-medium text-slate-300 mb-2">
          GitHub Organization/Account
        </label>
        <select
          value={selectedInstallationId || ''}
          onChange={(e) => handleInstallationChange(e.target.value)}
          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select an installation...</option>
          {installations.map((installation) => (
            <option key={installation.id} value={installation.id}>
              {installation.accountLogin} ({installation.accountType})
            </option>
          ))}
        </select>
      </div>

      {/* Repository List */}
      {selectedInstallationId && (
        <div className="p-4">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search repositories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Loading State */}
          {reposLoading && (
            <div className="flex items-center justify-center gap-2 text-slate-400 py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading repositories...</span>
            </div>
          )}

          {/* Error State */}
          {reposError && (
            <div className="text-red-400 text-sm py-4">
              Failed to load repositories. Please try again.
            </div>
          )}

          {/* Repository List */}
          {!reposLoading && !reposError && (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {filteredRepos.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  {searchQuery ? 'No repositories match your search' : 'No repositories available'}
                </p>
              ) : (
                filteredRepos.map((repo) => {
                  const isSelected = selectedRepoNames.has(repo.name);
                  return (
                    <RepositoryItem
                      key={repo.id}
                      repo={repo}
                      isSelected={isSelected}
                      onToggle={handleRepoToggle}
                    />
                  );
                })
              )}
            </div>
          )}

          {/* Selection Summary */}
          {selectedRepoNames.size > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                {selectedRepoNames.size} {multiSelect ? 'repositories' : 'repository'} selected
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
